import React from 'react';
import PropTypes from 'prop-types';

import { Button } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  black,
  darkSlate,
  ghostWhite,
  grape,
  lightSlate,
  shadowMedium,
  slateGrey,
} from 'lib/css/colors';
import BrandCard from 'components/codify_db/BrandCard';
import ManufacturerCard from 'components/codify_db/ManufacturerCard';
import UPBProductCatalogCard from 'components/upb_viewer/UPBProductCatalogCard';
import { VIEW_MODES, EMPTY_STATE_MESSAGES } from 'components/codify_db/constants';

function CodifyDBCatalogView({
  viewMode,
  manufacturers,
  brands,
  products,
  productCount,
  productType,
  selectedManufacturer,
  selectedBrand,
  onManufacturerClick,
  onBrandClick,
  onBackClick,
  onDistributorClick,
}) {
  const renderBreadcrumb = () => {
    if (!selectedManufacturer && !selectedBrand) return null;

    return (
      <div className={classNames(css(styles.breadcrumb), 'mb-4')}>
        <Button variant="link" className={css(styles.backButton)} onClick={onBackClick}>
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back
        </Button>
        <div className={css(styles.breadcrumbText)}>
          {selectedManufacturer && (
            <span>
              <strong>{selectedManufacturer.name}</strong>
              {selectedBrand && (
                <span>
                  {' '}
                  / <strong>{selectedBrand.name}</strong>
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    // Show manufacturers
    if (viewMode === VIEW_MODES.MANUFACTURERS) {
      return manufacturers.length ? (
        manufacturers.map(manufacturer => (
          <ManufacturerCard
            key={manufacturer.id}
            manufacturer={manufacturer}
            onManufacturerClick={onManufacturerClick}
            onDistributorClick={onDistributorClick}
          />
        ))
      ) : (
        <div className={css(styles.emptyState)}>
          {EMPTY_STATE_MESSAGES[VIEW_MODES.MANUFACTURERS]}
        </div>
      );
    }

    // Show brands for selected manufacturer
    if (viewMode === VIEW_MODES.BRANDS) {
      return brands.length ? (
        brands.map(brand => (
          <BrandCard
            key={brand.id}
            brand={brand}
            onBrandClick={onBrandClick}
            onDistributorClick={onDistributorClick}
          />
        ))
      ) : (
        <div className={css(styles.emptyState)}>{EMPTY_STATE_MESSAGES[VIEW_MODES.BRANDS]}</div>
      );
    }

    // Show products for selected brand
    if (viewMode === VIEW_MODES.PRODUCTS) {
      return productCount ? (
        products.map(product => (
          <UPBProductCatalogCard key={product.id} product={product} productType={productType} />
        ))
      ) : (
        <div className={css(styles.emptyState)}>{EMPTY_STATE_MESSAGES[VIEW_MODES.PRODUCTS]}</div>
      );
    }

    return null;
  };

  return (
    <div className={css(styles.catalogWrapper)}>
      {renderBreadcrumb()}
      <div className={css(styles.container)}>{renderContent()}</div>
    </div>
  );
}

const styles = StyleSheet.create({
  catalogWrapper: {
    width: '100%',
    padding: '0 20px',
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: 500,
    gap: '8px',
    padding: '20px 0',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: ghostWhite,
    padding: '12px 20px',
    borderRadius: '10px',
    border: '1px solid ' + lightSlate,
    boxShadow: '0 1px 3px ' + shadowMedium,
  },
  backButton: {
    padding: '0',
    marginRight: '15px',
    textDecoration: 'none',
    color: grape,
    fontSize: '16px',
    fontWeight: 500,
    transition: 'color 0.2s ease',
    ':hover': {
      textDecoration: 'none',
      color: black,
    },
  },
  breadcrumbText: {
    fontSize: '16px',
    color: darkSlate,
    fontWeight: 500,
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: slateGrey,
    fontSize: '18px',
    fontWeight: 500,
    width: '100%',
  },
});

CodifyDBCatalogView.propTypes = {
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)).isRequired,
  manufacturers: PropTypes.array,
  brands: PropTypes.array,
  products: PropTypes.array,
  productCount: PropTypes.number,
  productType: PropTypes.string,
  selectedManufacturer: PropTypes.object,
  selectedBrand: PropTypes.object,
  onManufacturerClick: PropTypes.func,
  onBrandClick: PropTypes.func,
  onBackClick: PropTypes.func,
  onDistributorClick: PropTypes.func,
};

CodifyDBCatalogView.defaultProps = {
  manufacturers: [],
  brands: [],
  products: [],
  productCount: 0,
};

export default CodifyDBCatalogView;
